/* Function related to core map rendering, legend */
import axios from 'axios';
import * as turf from '@turf/turf';

export function generateAOIConfig (layer, borderColor) {
  let { id, geom } = layer;
  try {
    geom = JSON.parse(geom);
  } catch (e) {
  }
  const config = { // default boundary
    id: id + '',
    type: 'line',
    source: {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: geom
      }
    },
    layout: {},
    paint: {
      'line-color': borderColor,
      'line-width': 3,
      'line-offset': 0
    }
  }
  return config;
}

export function generateConfig (layer, config) {
  let { id, settings, geom } = layer;

  if (!config) {
    try {
      geom = JSON.parse(geom);
    } catch (e) {
    }

    config = {
      id: id + '',
      type: 'fill',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: geom
        }
      },
      layout: {},
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.8,
        'fill-outline-color': 'rgba(169,169,169,1)'
      }
    };
  }
  config.paint = config.paint || {};
  Object.keys(settings || {})
    .forEach(key => {
      switch (key) {
        case 'color':
          config.paint['fill-color'] = '#' + settings[key];
          break;
        case 'opacity':
        case 'fill_opacity':
          config.paint['fill-opacity'] = (parseInt(settings[key]) / 100);
          break;
        default:
          return null;
      }
    });

  Object.keys(settings?.appearance || {})
    .forEach(key => {
      const enabled = settings?.appearance[key];
      switch (key) {
        case 'fill':
          if (!enabled) {
            config.paint['fill-opacity'] = 0; // to hide fill
          }
          break;
        case 'border':
          config.paint['fill-outline-color'] = enabled ? 'rgba(169,169,169,0)' : 'rgba(169,169,169,1)';
          break;
        default:
          return null;
      }
    });

  return config;
}
/* for some AOI, user provided fill in pattern image, so in addition to the border, we add a layer (suffix fill), to the map
*/
export function generateCustomFillConfig (map, layer, borderColor, fillImageUrl) {
  let { id, geom } = layer;
  try {
    geom = JSON.parse(geom);
  } catch (e) {
  }
  // Load an image to use as the pattern
  map.loadImage(
    fillImageUrl, (err, image) => {
      // Throw an error if something went wrong
      if (err) {
        console.log(err);
      };

      // Declare the image
      map.addImage('pattern', image);

      // Use it
      map.addLayer({
        id: id + 'aoifill',
        type: 'fill',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: geom
          }
        },
        paint: {
          'fill-pattern': 'pattern'
        }
      });
    });
  return generateAOIConfig(layer, borderColor);
}
/* mask out other areas, only make the AOI visible */
export function drawMask (map, geom) {
  return new Promise((resolve) => {
    const world = turf.bboxPolygon([-180, -90, 180, 90]);
    const diff = turf.difference(world, geom);

    const config = {
      id: 'world',
      source: {
        type: 'geojson',
        data: diff
      },
      type: 'fill',
      visibility: 'visible',
      paint: {
        'fill-color': 'white',
        'fill-opacity': 1
      }
    };

    map.addLayer(config);
  })
}

// export function switchToLayer(mapContainerId, map, layerIds, id) {
//     $('#' + mapContainerId + ' .lds-spinner').show();
//     layerIds.forEach(x => {
//         map.setLayoutProperty(x, 'visibility', 'none');
//     });

//      map.setLayoutProperty(id, 'visibility', 'visible');
// }
/*
combine border and  fill to toggle visibilty
*/
export function toggleLayer (map, id, isVisible) {
  if (isVisible) {
    showLayer(map, String(id));
  } else {
    hideLayer(map, String(id));
  }
}
function hideLayer (map, id) {
  changeLayerVisibility(map, id, false);
  changeLayerVisibility(map, id + 'fill', false);
}

function showLayer (map, id) {
  changeLayerVisibility(map, id, true);
  changeLayerVisibility(map, id + 'fill', true);
}

function changeLayerVisibility (map, id, toVisible) {
  if (map.style._layers[id]) {
    const visibility = map.getLayoutProperty(id, 'visibility'); // initial is is undefined
    if (visibility) {
      if (visibility === 'visible' && !toVisible) {
        map.setLayoutProperty(id, 'visibility', 'none');
      } else if (visibility !== 'visible' && toVisible) {
        map.setLayoutProperty(id, 'visibility', 'visible');
      }
    } else { // initally visibility is undefined, so can't use that to check if layer is visible or not, just follow the instruction to hide/show it.
      map.setLayoutProperty(id, 'visibility', (toVisible ? 'visible' : 'none'));
    }
  }
}
/* copied from mapping portal */
export function checkSourceLoaded (id, map) {
  return new Promise((resolve, reject) => {
    let time = 0;
    const timer = setInterval(() => {
      try {
        if (map.getSource(id) && map.isSourceLoaded(id)) {
          clearInterval(timer)
          resolve(id)
        }

        time += 500;

        // timeout
        if (!map.getSource(id) || time > 30000) {
          clearInterval(timer)
          resolve(id);
        }
      } catch (e) {
        clearInterval(timer)
        reject(e)
      }
    }, 500)
  });
}
const makeStepSegments = (colorExpression, valueBoundary) => {
  const segments = [];
  /* skip first two elements since they describe the type of expression and its input
  * while the rest are pairs of a color and a respective step threshold
  * https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step */

  /* valueBoundary is a customized field, to indicator where the min and max has boundary,
  if no boundary display as < min, and > max */
  /* used to -1 to avoid overlapping category values, however, sometimes, the values are 0.1 increment, so -1 can
  cause big issues. */
  let precision = 0;
  for (let i = 2; i < colorExpression.length - 2; i += 2) {
    const valueStr = colorExpression[i + 1] + '';
    precision = Math.max(precision, valueStr.includes('.') ? valueStr.split('.')[1].length : 0);
  }
  const defaultGap = Math.pow(10, -1 * precision);
  for (let i = 2; i < colorExpression.length; i += 2) {
    const color = colorExpression[i];
    const nextColor = colorExpression[i + 2];
    if (color === nextColor) continue;

    const prevStep = colorExpression[i - 1];
    const nextStep = colorExpression[i + 1];

    let min = prevStep;
    let max = nextStep - defaultGap;
    // special case that handles the min value of the very first segment, here assumed all increments are the same

    if (i === 2 && colorExpression.length > 5) {
      if (valueBoundary && valueBoundary.min === false) {
        min = Number.NEGATIVE_INFINITY;
        max = nextStep; // for < no need to remove the gap
      } else {
        const increment = colorExpression[i + 3] - nextStep;
        // slightly different calulcation depending on whether the values are positive or negative
        if (nextStep < 0) { min = nextStep - increment; } else { min = (nextStep - defaultGap) - increment; } //
      }
    }

    // special case that handles the max value of the last segment
    if (i === colorExpression.length - 1) {
      if (valueBoundary && valueBoundary.max === false) {
        max = Number.POSITIVE_INFINITY;
        min = (prevStep - defaultGap);
      } else {
        const increment = prevStep - colorExpression[i - 3];
        max = (prevStep - defaultGap) + increment;
      }
    }

    if (precision > 0) { // with decimal place, format the values;
      min = (min !== Number.NEGATIVE_INFINITY) ? min.toFixed(precision) : Number.NEGATIVE_INFINITY;
      max = (max !== Number.POSITIVE_INFINITY) ? max.toFixed(precision) : Number.POSITIVE_INFINITY;
    }
    /* don't calculate the range on the fly, calculate once and keep in the array */
    let range;
    if (Number.NEGATIVE_INFINITY === (min)) {
      range = '<' + max;
    } else if (Number.POSITIVE_INFINITY === max) {
      range = '>' + min;
    } else {
      range = min === max ? `${min}` : `${min} - ${max}`;
    }

    segments.push({ color, min, max, range });
  }
  // return {segments, step: defaultGap};
  return segments;
};
function makeLayerSegments (layers) {
  const segments = [];

  for (let i = 0; i < layers.length; i++) {
    const type = layers[i].type;
    const color = layers[i].paint[type + '-color'];
    if (typeof color !== 'string') continue;
    const category = layers[i].id;
    segments.push({ category, color });
  }

  return segments;
}

const makeMatchSegments = (colorExpression, customLabel, sizeExpression) => {
  const segments = [];
  /* skip first two elements since they describe the type of expression and its input
  * skip the last element since it's just a fallback color
  * https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-match */
  for (let i = 2; i < colorExpression.length - 1; i += 2) {
    const color = colorExpression[i + 1];
    const category = colorExpression[i];
    const size = sizeExpression ? (sizeExpression[category] ? sizeExpression[category] : sizeExpression.other) : '';
    if (customLabel && customLabel[category]) {
      segments.push({ category: customLabel[category], color, size });
    } else {
      segments.push({ category, color, size });
    }
  }
  return segments;
};

/* copied from MP, collect legend info into legend object */
export function collectLegend (inLayers) {
  let legend = {};
  const layers = inLayers.filter(l => l.hide_legend !== true)
  if (layers.length > 1) {
    legend = {
      type: 'category',
      segments: makeLayerSegments(layers),
      unit: '',
      symbol: layers[0] && layers[0].type
    };
    return legend;
  }

  const layer = layers[0];
  if (!layer.paint) { return null; }

  const colorExpression = layer.paint[layer.type + '-color'];
  if (!colorExpression || !(colorExpression instanceof Array)) { return null; }

  // don't generate a legend when *-color is not a data expression (e.g. step expression based on zoom level [ "step", ["zoom"], ... ])
  // https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#data-expressions
  if (colorExpression[1][0] !== 'get') { return null; }
  // when symbols are circle, check the circle-radius to rendering different circle sizes in legend.
  // Note: will not work for all cases/zoom levels (as legend sizes are fixed, but map rendering can be different size)
  const sizeExpression = {};
  if (layer.type === 'circle' && layer.paint[layer.type + '-radius']) {
    const sizeList = layer.paint[layer.type + '-radius'].find(x => x[0] === 'match'); //  || x[0] === 'step');
    for (let i = 2; i < sizeList.length; i += 2) {
      // first one is type_Id, second one is size
      if (i + 1 > sizeList.length - 1) { // last one
        sizeExpression.other = sizeList[i];
      } else {
        const size = sizeList[i + 1];
        const category = sizeList[i];
        sizeExpression[category] = size;
      }
    }
  }
  switch (colorExpression[0]) {
    case 'step': {
      const boundary = layer.value_boundary || {};
      const segments = makeStepSegments(colorExpression, boundary);
      legend = {
        type: 'range',
        segments,
        unit: '%',
        min: segments[0].min,
        max: segments[segments.length - 1].max,
        symbol: layer.type
      };

      if (boundary.min === false) {
        legend.min = segments[0].max;
        legend.minBoundary = false;
      }

      if (boundary.max === false) {
        legend.max = segments[segments.length - 1].min;
        legend.maxBoundary = false;
      }
      break;
    }
    case 'match':
      legend = {
        type: 'category',
        segments: makeMatchSegments(colorExpression, layer.custom_legend_label, sizeExpression),
        unit: '',
        symbol: layer.type
      };
      break;
    default:
      break;
  }

  return legend;
};
export function getBBox (geom) {
  return turf.bbox(geom);
}

export function zoomToLayer (map, mapLayers, layerId) {
  return new Promise((resolve) => {
    if (layerId && mapLayers[layerId]) {
      let bbox;
      if (mapLayers[layerId].bbox) {
        bbox = mapLayers[layerId].bbox;
      } else {
        const geoms = Object.values(mapLayers[layerId].layer).map(layer => ({ type: 'Feature', geometry: layer.geom }));
        bbox = turf.bbox(geoms[0]);
        mapLayers[layerId].bbox = bbox;
      }
      map.fitBounds(bbox);
    }
  })
}

/* Idea from Stack Overflow https://stackoverflow.com/a/51683226  */
export class MapboxGLButtonControl {
  constructor ({
    className = '',
    title = '',
    eventHandler = evtHndlr
  }) {
    this._className = className;
    this._title = title;
    this._eventHandler = eventHandler;
  }

  onAdd (map) {
    this._btn = document.createElement('button');
    this._btn.className = this._className;
    this._btn.type = 'button';
    this._btn.title = this._title;
    this._btn.onclick = this._eventHandler;
    this._icon = document.createElement('span');
    this._icon.className = 'mapboxgl-ctrl-icon';
    this._btn.appendChild(this._icon);

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl-group mapboxgl-ctrl';
    this._container.appendChild(this._btn);

    return this._container;
  }

  onRemove () {
    this._container.parentNode.removeChild(this._container);
    this._map = undefined;
  }
}
/* get layer information: id, name,stying, geojson, tiles etc */
export async function loadDataFromUrl (id, isAOI) {
  const headers = { 'x-api-key': import.meta.env.VITE_APP_MAP_API_KEY };
  const datasetPrefix = import.meta.env.VITE_APP_MAP_URL + 'layers/';
  const datasetSuffix = '?type=' + (isAOI ? 'aoi' : 'dataset') + '&gridId=1&id=';
  const url = datasetPrefix + id + datasetSuffix + id;
  const responseBody = await axios.get(url, { headers });
  const baseLayer = responseBody.data.data;
  return baseLayer;
}
